import classNames from 'classnames';
import { addMinutes, endOfISOWeek, format, startOfISOWeek } from 'date-fns';
import { FC, Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import {
  Area,
  AreaChart,
  Brush,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { ReactComponent as CourseDownIcon } from 'assets/images/icons/course_down.svg';
import { ReactComponent as CourseUpIcon } from 'assets/images/icons/course_up.svg';

import ButtonGroup from 'ui/ButtonGroup';

import CustomTooltip from 'modules/SensorDetails/CustomTooltip';
import DateChanger from 'modules/SensorDetails/DateChanger';
import InfoCard from 'modules/SensorDetails/InfoCard';

import { SensorBattery, SensorStatus, SensorTrends } from 'entities/common';
import { DateTypes, SensorTabs } from 'entities/view/sensorDetails';

import { getSensorFullChart, getSensorMetrics } from 'services/sensors';
import { GetFullChartResponse, GetMetricsResponse } from 'store/SensorDetails/SensorDetails.type';

import {
  FULL_GRAPH_AXIS_BY_SENSOR,
  QUERY_PARAMS,
  SENSOR_TYPES,
  SensorMeasurement,
} from 'utils/constants';
import {
  formatLocalClock,
  formatLocalDateTime,
  formatLocalDayMonth,
  getBrowserTimeZone,
  parseServerDate,
  QueryHelper,
  StringHelper,
} from 'utils/helpers';
import getType from 'utils/helpers/getType';

type SensorCardProps = {
  sensorId: number;
  onHourClick?: (hour: string, chartDate: Date) => void;
};

type TabItem = {
  title: React.ReactNode;
  subtitle: string;
  value: string;
  Icon?: React.ReactNode;
};

const INITIAL_METRICS: GetMetricsResponse = {
  items: [],
  sensor: null,
  meta: { total: 0, skip: 0, limit: 0, page: 0, pages: 0 },
};

const SensorCard: FC<SensorCardProps> = ({ sensorId, onHourClick }) => {
  const [fullChartData, setFullChartData] = useState<GetFullChartResponse | null>(null);
  const [metrics, setMetrics] = useState<GetMetricsResponse>(INITIAL_METRICS);
  const [isChartLoading, setIsChartLoading] = useState(false);

  const [activeDot, setActiveDot] = useState<number | null>(null);
  const [date, setDate] = useState<Date | [Date, Date]>(new Date());
  const [selectedType, setSelectedType] = useState(DateTypes.DAY);
  const [selectedTab, setSelectedTab] = useState<string>(SensorTabs.ALL);
  const [tabChanged, setTabChanged] = useState(false);
  const [nextUpdate, setNextUpdate] = useState(addMinutes(new Date(), 10));

  const sensorType = useMemo(
    () => SENSOR_TYPES?.find((item) => item.value === metrics?.sensor?.sensor_type)?.label || '-',
    [metrics],
  );

  const sensorMeta = useMemo(
    () => [
      {
        label: 'Status',
        value:
          metrics?.sensor?.is_deleted || !metrics?.sensor?.active
            ? '-'
            : !metrics.items?.length || metrics.items[0]?.online_status == null
              ? '-'
              : StringHelper.capitalizeFirstLetter(
                  (metrics.items[0]?.online_status ? SensorStatus.ONLINE : SensorStatus.OFFLINE) ||
                    '',
                ),
        className:
          metrics?.sensor?.is_deleted || metrics?.sensor?.active === false
            ? 'zone-details__meta-value--offline'
            : metrics.items?.[0]?.online_status == null
              ? ''
              : metrics.items?.[0]?.online_status
                ? 'zone-details__meta-value--online'
                : 'zone-details__meta-value--offline',
      },
      {
        label: 'Battery',
        value:
          metrics?.sensor?.is_deleted || !metrics?.sensor?.active
            ? '-'
            : !metrics.items?.length || metrics.items[0]?.battery_status == null
              ? '-'
              : StringHelper.capitalizeFirstLetter(
                  (metrics.items[0]?.battery_status ? SensorBattery.HIGH : SensorBattery.LOW) || '',
                ),
        isBattery: true,
        batteryHigh: metrics.items?.[0]?.battery_status != null && !!metrics.items?.[0]?.battery_status,
      },
      {
        label: 'Coordinates',
        value:
          !metrics.items?.length || !metrics?.sensor?.active || metrics?.sensor?.is_deleted ||
          metrics.items[0]?.latitude == null || metrics.items[0]?.longitude == null
            ? '-'
            : `${Number(metrics.items[0]?.latitude).toFixed(2)}°, ${Number(metrics.items[0]?.longitude).toFixed(2)}°`,
      },
      {
        label: 'Last update',
        value: metrics?.sensor?.is_deleted
          ? 'Deleted'
          : !metrics?.sensor?.active
            ? 'Turned off'
            : metrics.items?.[0]?.time
              ? formatLocalDateTime(metrics.items[0].time)
              : '-',
      },
    ],
    [metrics],
  );

  const tabs = useMemo((): TabItem[] => {
    const prevPoints = metrics?.items?.[1]?.points || [];
    const prevValueById: Record<string, number> = {};

    for (const point of prevPoints) {
      prevValueById[String(point.measurement_id)] = point.measurement_value;
    }

    const metricsTabs: TabItem[] =
      metrics?.items?.[0]?.points?.map((item) => {
        const prevValue = prevValueById[String(item.measurement_id)];
        let trend: SensorTrends | null = null;

        if (typeof prevValue === 'number') {
          if (item.measurement_value > prevValue) {
            trend = SensorTrends.UP;
          } else if (item.measurement_value < prevValue) {
            trend = SensorTrends.DOWN;
          }
        }

        return {
          title: (
            <>
              {metrics?.sensor?.active && !metrics?.sensor?.is_deleted
                ? item.measurement_value
                : '-'}
              {metrics?.sensor?.active && !metrics?.sensor?.is_deleted && (
                <span className="zone-details__info-size">{item.measurement_unit}</span>
              )}
            </>
          ),
          subtitle: item.measurement_name,
          value: item.measurement_id,
          Icon:
            trend && metrics?.sensor?.active && !metrics?.sensor?.is_deleted ? (
              trend === SensorTrends.UP ? (
                <CourseUpIcon />
              ) : (
                <CourseDownIcon />
              )
            ) : null,
        };
      }) || [];

    const sensorTypeKey = metrics?.sensor?.sensor_type || '';
    if (metricsTabs.length > 1 && FULL_GRAPH_AXIS_BY_SENSOR[sensorTypeKey]) {
      metricsTabs.push({
        title: 'Full graph',
        subtitle: 'Graph',
        value: SensorTabs.ALL,
      });
    }

    return metricsTabs;
  }, [metrics]);

  const { subtitle, title } = useMemo(() => {
    let title = '';
    let subtitle = '';

    if (selectedTab === SensorTabs.ALL) {
      title = 'Full graph';
      subtitle = '';
    } else {
      const currentTab =
        SensorMeasurement?.[selectedTab as unknown as keyof typeof SensorMeasurement];
      title = currentTab?.label || '';
      subtitle = currentTab?.unit || '';
    }

    return { title, subtitle };
  }, [selectedTab]);

  const latestMetricTime = useMemo(
    () => (metrics?.items?.[0]?.time ? parseServerDate(metrics.items[0].time) : null),
    [metrics],
  );

  const points = useMemo(() => {
    const isDay = selectedType === DateTypes.DAY;

    if (!fullChartData?.series || !fullChartData.series.length) {
      return [];
    }

    const now = new Date();
    const cutoff = latestMetricTime && latestMetricTime < now ? latestMetricTime : now;
    let isCurrentPeriodSelected = false;

    if (selectedType === DateTypes.DAY && getType(date) === 'date') {
      isCurrentPeriodSelected = format(date as Date, 'dd.MM.yyyy') === format(now, 'dd.MM.yyyy');
    } else if (selectedType === DateTypes.WEEK && getType(date) === 'array') {
      const [start, end] = date as [Date, Date];
      isCurrentPeriodSelected = start <= now && now <= end;
    } else if (selectedType === DateTypes.MONTH && getType(date) === 'date') {
      isCurrentPeriodSelected = format(date as Date, 'MM.yyyy') === format(now, 'MM.yyyy');
    }

    const data = fullChartData.series.flatMap((item) =>
      item.points.map((element) => {
        const bucketStart = parseServerDate(element.bucket_start);

        const basePoint: { [x: string]: string | number | null; time: string } = {
          time: isDay
            ? formatLocalClock(element.bucket_start)
            : formatLocalDayMonth(element.bucket_start),
        };

        if (!isCurrentPeriodSelected || bucketStart <= cutoff) {
          basePoint[item.measurement_id] = element.value;
        }

        return basePoint;
      }),
    );

    const mergedData = mergeByTime(data);

    if (isDay) {
      const baseDayPoints: any[] = [];
      for (let i = 0; i < 24; i++) {
        const d = new Date(2000, 0, 1, i, 0);
        const timeStr = format(d, 'HH:mm');
        const match = (mergedData || [])?.find((m) => m.time === timeStr);
        baseDayPoints.push(match || { time: timeStr });
      }
      return baseDayPoints;
    }

    return mergedData;
  }, [fullChartData?.series, selectedType, date, latestMetricTime]);

  const renderTab = useMemo(() => {
    if (selectedTab === SensorTabs.ALL) {
      return tabs?.filter((item) => item.value !== SensorTabs.ALL);
    }
    return tabs?.filter((item) => item.value === selectedTab);
  }, [selectedTab, tabs]);

  const rawSensorType = metrics?.sensor?.sensor_type || '';

  const yAxisDomain = useMemo((): [number, number] | undefined => {
    if (!points.length) return undefined;

    if (selectedTab === SensorTabs.ALL) {
      const fullGraphConfig = FULL_GRAPH_AXIS_BY_SENSOR[rawSensorType];
      if (!fullGraphConfig) return undefined;

      const { step, min: axisMin, max: axisMax } = fullGraphConfig;
      const allKeys = renderTab.map((t) => t.value);
      const values = points.flatMap((p) =>
        allKeys.map((k) => p[k]).filter((v): v is number => typeof v === 'number' && !isNaN(v)),
      );

      if (!values.length) return undefined;

      const dataMin = Math.min(...values);
      const dataMax = Math.max(...values);

      const yMin = Math.floor(dataMin / step) * step;
      const yMax = Math.ceil(dataMax / step) * step;

      return [Math.max(yMin, axisMin), Math.min(yMax, axisMax)];
    }

    const config = SensorMeasurement[selectedTab];
    if (!config?.axisStep) return undefined;

    const step = config.axisStep;
    const values = points
      .map((p) => p[selectedTab])
      .filter((v): v is number => typeof v === 'number' && !isNaN(v));

    if (!values.length) return undefined;

    const dataMin = Math.min(...values);
    const dataMax = Math.max(...values);

    const yMin = Math.floor(dataMin / step) * step;
    const yMax = Math.ceil(dataMax / step) * step;

    return [
      Math.max(yMin, config.axisMin ?? -Infinity),
      Math.min(yMax, config.axisMax ?? Infinity),
    ];
  }, [selectedTab, points, rawSensorType, renderTab]);

  const handleSelectTab = (tab: string) => () => {
    setSelectedTab(tab);
  };

  const handleSelectType = (type: string) => {
    setSelectedType(type as DateTypes);

    if (type === DateTypes.DAY || type === DateTypes.MONTH) {
      setDate(new Date());
    } else {
      setDate([startOfISOWeek(new Date()), endOfISOWeek(new Date())]);
    }
  };

  const handleChangeDate = (value: Date | [Date, Date]) => {
    setDate(value);
  };

  // Fetch metrics
  const fetchMetrics = useCallback(() => {
    if (!sensorId) return;
    getSensorMetrics({
      sensor_id: sensorId,
      query: QueryHelper.getQueryString({
        [QUERY_PARAMS.LIMIT]: 2,
      }),
    })
      .then((res) => setMetrics(res.data))
      .catch(() => {});
  }, [sensorId]);

  // Fetch chart data
  const fetchChart = useCallback(() => {
    if (!sensorId) return;
    setIsChartLoading(true);
    const baseDate = getType(date) === 'date' ? (date as Date) : (date as [Date, Date])[0];

    getSensorFullChart({
      sensor_id: sensorId,
      query: QueryHelper.getQueryString({
        [QUERY_PARAMS.MODE]: selectedType,
        [QUERY_PARAMS.TARGET_DATE]: new Date(
          format(new Date(baseDate), 'MM/dd/yyyy 12:00:00'),
        ).toISOString(),
        [QUERY_PARAMS.TZ]: getBrowserTimeZone(),
      }),
    })
      .then((res) => {
        setFullChartData(res.data);
        setIsChartLoading(false);
      })
      .catch(() => {
        setIsChartLoading(false);
      });
  }, [sensorId, selectedType, date]);

  // Fetch metrics on mount / sensorId change
  useEffect(() => {
    fetchMetrics();
  }, [fetchMetrics]);

  // Fetch chart on mount / selectedType / date / sensorId change
  useEffect(() => {
    fetchChart();
  }, [fetchChart]);

  // Auto-refresh every 10 minutes
  useEffect(() => {
    let timer: NodeJS.Timeout;

    if (nextUpdate.valueOf() > new Date().valueOf()) {
      timer = setTimeout(() => {
        fetchMetrics();
        fetchChart();
        setNextUpdate(addMinutes(new Date(), 10));
      }, nextUpdate.valueOf() - new Date().valueOf());
    } else {
      setNextUpdate(addMinutes(new Date(), 10));
    }

    return () => clearTimeout(timer);
  }, [nextUpdate, fetchMetrics, fetchChart]);

  useEffect(() => {
    if (!tabChanged && tabs?.length) {
      setSelectedTab(tabs[0].value);
      setTabChanged(true);
    }
  }, [tabs, tabChanged]);

  const tooltipContent = useCallback(
    (props: any) => (
      <CustomTooltip
        setActiveDot={setActiveDot}
        isFullGraph={selectedTab === SensorTabs.ALL}
        {...props}
      />
    ),
    [setActiveDot, selectedTab],
  );

  return (
    <div className="zone-details__sensor-card">
      <div className="zone-details__sensor-card-header">
        <div className="zone-details__sensor-card-type">Sensor type: {sensorType}</div>
        <div className="zone-details__sensor-card-meta">
          {sensorMeta.map((item, index) => (
            <div key={index} className="zone-details__sensor-card-meta-item">
              <div className="zone-details__meta-label">{item.label}</div>
              <div className={classNames('zone-details__meta-value', item.className)}>
                {item.value}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="zone-details__sensor-card-body">
        <div className="zone-details__metric-row">
          {tabs.map((item, index) => (
            <InfoCard
              key={index}
              {...item}
              active={selectedTab === item.value}
              onClick={handleSelectTab(item.value)}
            />
          ))}
        </div>

        <div className="zone-details__chart-area">
          <div className="zone-details__chart-headline">
            <h2>{title}</h2>
            <span>{subtitle}</span>
          </div>

          <div className="zone-details__chart-controls">
            <ButtonGroup
              className="zone-details__chart-group"
              active={selectedType}
              buttons={[
                { text: 'Day', id: DateTypes.DAY },
                { text: 'Week', id: DateTypes.WEEK },
                { text: 'Month', id: DateTypes.MONTH },
              ]}
              onClick={handleSelectType}
            />

            <DateChanger date={date} onChange={handleChangeDate} type={selectedType} />
          </div>

          <div className="zone-details__chart-container">
            {!isChartLoading && renderTab.length > 0 && (
              <ResponsiveContainer
                key={`${sensorId}-${selectedTab}-${selectedType}`}
                width="100%"
                height="100%"
              >
                <AreaChart
                  data={points}
                  onMouseLeave={() => setActiveDot(null)}
                  onClick={
                    selectedType === DateTypes.DAY && onHourClick
                      ? (e: any) => {
                          if (e?.activeLabel) {
                            const chartDate = Array.isArray(date) ? date[0] : date;
                            onHourClick(e.activeLabel, chartDate);
                          }
                        }
                      : undefined
                  }
                  style={
                    selectedType === DateTypes.DAY && onHourClick
                      ? { cursor: 'pointer' }
                      : undefined
                  }
                >
                  <XAxis dataKey="time" axisLine={false} tickLine={false} />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    dataKey={selectedTab === SensorTabs.ALL ? undefined : selectedTab}
                    domain={yAxisDomain}
                    allowDataOverflow={!!yAxisDomain}
                  />

                  <Tooltip content={tooltipContent} />

                  {renderTab.map((item) => {
                    const measureKey = selectedTab === SensorTabs.ALL ? item.value : selectedTab;
                    const color = SensorMeasurement?.[measureKey]?.color || '#00C897';

                    return (
                      <Fragment key={item.value}>
                        <defs>
                          <linearGradient
                            id={`${sensorId}-${item.value}`}
                            x1="0"
                            y1="0"
                            x2="0"
                            y2="1"
                          >
                            <stop offset="0" stopColor={color} stopOpacity={1} />
                            <stop offset="1" stopColor={color} stopOpacity={0} />
                          </linearGradient>
                        </defs>

                        <Area
                          type="linear"
                          dataKey={item.value}
                          stroke={color}
                          fill={`url(#${sensorId}-${item.value})`}
                          fillOpacity={0.2}
                        />

                        {!!activeDot && selectedTab !== SensorTabs.ALL && (
                          <ReferenceLine y={activeDot} stroke={color} />
                        )}
                      </Fragment>
                    );
                  })}

                  <Brush
                    dataKey="time"
                    height={16}
                    stroke={
                      SensorMeasurement?.[
                        selectedTab === SensorTabs.ALL ? renderTab[0]?.value : selectedTab
                      ]?.color || '#00C897'
                    }
                    travellerWidth={16}
                    fill="rgba(0, 0, 0, 0.05)"
                    style={{ userSelect: 'none' }}
                    className="zone-details__chart-brush"
                  />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

function mergeByTime(
  data: { [x: string]: string | number | null; time: string }[],
): Array<Record<string, any>> {
  const map = new Map<string, Record<string, any>>();

  for (const item of data) {
    const time = item.time;
    if (!map.has(time)) {
      map.set(time, { time });
    }
    Object.assign(map.get(time) || {}, item);
  }

  return Array.from(map.values());
}

export default SensorCard;
